import { useState, useEffect, SetStateAction, Dispatch } from 'react'
import { ConfigDataType } from '../../../Organisms/Form/types'
import utils from '../../../Organisms/Form/utils'
import { ApiData } from '../../../../views/Pages/Onboarding/types'
import { ChangeArgs } from '../../../Atoms/Dropdown/types'
import { useDropdownOptions } from '../../Context/OnboardingContext'

export interface InitialStateValue {
  value: string;
  required?: boolean;
  show?: boolean;
  pattern?: RegExp;
  visual?: boolean;
  secondaryName?: string;
}

export interface InitialState {
  [key: string]: InitialStateValue;
}

export interface OnSubmitParams {
  data: ApiData,
  options?: {contextType?: string, contentType?: string},
  configData?: ConfigDataType,
  setConfigData?: Dispatch<SetStateAction<ConfigDataType>>
}

const MULTIPLE_SELECTION_FIELDS = ['curenciesInternationalPayments']
const IBAN_FIELDS = ['ibanAccountNumber']

const useForm = (
  fetchedData?: ApiData,
  config?: ConfigDataType,
  onSubmit?: ({data, options: { contentType }, configData, setConfigData}: OnSubmitParams) => void
) => {
  const { dropdownOptions } = useDropdownOptions()
  const [configData, setConfigData] = useState(config)
  const [formData, setFormData] = useState(
    utils.setInitialStateData(config, fetchedData, dropdownOptions) || {}
  )
  const [emptyValidation, setEmptyValidation] = useState(true)

  const emptyValidator = () => {

    const output: boolean = Object.values(formData)
      .map(obj => {
        if (obj.show && obj.required && typeof obj.value === 'string'){
          if(obj.secondaryName !== undefined){
            return (obj.secondaryName !== '' && obj.value.trim() === '')
          }
          return obj.value.trim() === ''
        }
        // @ts-ignore
        if ((obj.visualKey ? (obj[`${itemKey}`]?.value === 'true') : obj.show) && obj.required && typeof obj.value === 'object' && !Array.isArray(obj.value)){
          // @ts-ignore
          return obj.value === null || obj.value?.documentName === null
        }
        if(obj.show && Array.isArray(obj.value)){
          return obj.value.some(item => item.documentName === null)
        }
        if (obj.show && obj.required && typeof obj.value === 'object')
          return obj.value === null

        return false
      })
      .includes(true)
    setEmptyValidation(output)
  }

  useEffect(() => {
    emptyValidator()
  }, [formData])

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement> | ChangeArgs
  ): void => {
    // @ts-ignore:next-line
    const { name, value, required, checked } = event?.target || event
    const pattern: RegExp = formData?.[name as string]?.pattern
    if (value === '' || !pattern || value?.match(pattern)) {
      setFormData(prevstate => {
        const output = { ...prevstate }
        if (output?.[name as string]) {
          if(IBAN_FIELDS.includes(name)){
            output[name as string] = {
              value: value.toUpperCase(),
              required: true,
              show: true
            }
            return output
          } else if(MULTIPLE_SELECTION_FIELDS.includes(name)) {

            // @ts-ignore:next-line
            output[`${name}`].value = output[`${name}`].value || []
            // @ts-ignore:next-line
            const isSelectedIndex = output[`${name}`]?.value?.findIndex(item => item?.index === value?.index)
            if(isSelectedIndex > -1){
              // @ts-ignore:next-line
              output[`${name}`]?.value?.splice(isSelectedIndex,1)
            } else {
              // @ts-ignore:next-line
              output[`${name}`]?.value?.push(value)
            }
            return output
          }

          if(value?.message === 'add-input-file') {
            // @ts-ignore:next-line
            output[`${name}`].value.push({
              documentFileExtension: null,
              documentFileType: null,
              documentID: 0,
              documentName: null,
              documentSection: null,
              documentType: null,
            })
            if(output[`${name}`].value.length > value.numberOfRows) {
              // @ts-ignore:next-line
              output[`${name}`].value.pop()
            }
            return output
          }

          if (Array.isArray(output[`${name}`].value)) {
            if(output[`${name}`].value.length > 0){
              if(value.removeFile){
                // @ts-ignore:next-line
                const indexOfFile = output[`${name}`].value.findIndex(file => file.documentID === parseInt(value.documentID))
                // @ts-ignore:next-line
                output[`${name}`].value[`${indexOfFile}`] = {
                  documentFileExtension: null,
                  documentFileType: null,
                  documentID: 0,
                  documentName: null,
                  documentSection: null,
                  documentType: null,
                }
              } else if(value.removeRow){
                // @ts-ignore:next-line
                output[`${name}`].value.splice(value.indexOfFile, 1)
              } else {
                // @ts-ignore:next-line
                output[`${name}`].value[`${value.indexOfFile}`] = { documentName: value.name, documentID: value.documentID, hasVisual: value.visualKey ? true : false }
              }
            }
            return output
          }

          if(value && value.constructor === Object && !Array.isArray(output[`${name}`].value)) {
            // @ts-ignore:next-line
            output[`${name}`].value = value.removeFile
              ? {
                documentFileExtension: null,
                documentFileType: null,
                documentID: 0,
                documentName: null,
                documentSection: null,
                documentType: null,
              }
              : { documentName: value.name, hasVisual: value.visualKey ? true : false }
            return output
          }
          // @ts-ignore:next-line
          output[name as string].value = event.target?.attributes?.type?.nodeValue === 'checkbox' ? checked : (value || '')
          output[name as string].required = required

        } else {
          output[name as string] = {
            value: value,
            required: required,
          }
        }
        return output
      })
    }
  }

  // you would use removeVisuals only when you have visual components that toggle show/hide
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    // eslint-disable-next-line
    const cleanFormData: {[key: string]: any} = configData?.keysToRemove?.length > 0 ? utils.cleanFormData(formData, configData) : {...formData}
    const errorKeys = utils.runSubmitValidation(
      utils.getKeysToValidate(configData, cleanFormData)
    )
    setConfigData(utils.injectErrorPropToConfig(configData, errorKeys))
    const convertedFormData = utils.convertFormDataToPost(cleanFormData, configData, fetchedData, dropdownOptions)
    const dataToSend = configData.postHeaders ? utils.convertContentType(configData.postHeaders, convertedFormData) : convertedFormData
    if (errorKeys?.length === 0) {
      onSubmit({data: dataToSend, options: { contentType: configData?.postHeaders?.contentType }, configData, setConfigData})
    }
  }

  const toggleShowByName = (name: string, show: boolean) => {
    setFormData(prevstate => {
      const output = { ...prevstate }
      output[name as string].show = show
      if (show === false) {
        output[name as string].value = ''
      }
      return output
    })
  }

  const toggleShowByVisualName = (visualName: string, show: string) => {
    setFormData(prevstate => {
      const output = { ...prevstate }
      output[`${visualName}`].value = show
      return output
    })
  }

  const toggleShowBySecondaryName = (name: string, secondaryNameInfo: string) => {
    setFormData(prevstate => {
      const output = { ...prevstate }
      output[`${name}`].secondaryName = secondaryNameInfo
      return output
    })
  }

  return {
    formData,
    emptyValidation,
    handleInputChange,
    handleSubmit,
    toggleShowByName,
    toggleShowByVisualName,
    toggleShowBySecondaryName,
    configData,
    setFormData
  }
}

export default useForm
